import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import {
  rebillCreditService,
  type RebillCreditListRow,
} from '../../../services/rebillCredit.service';

/**
 * Rebill/Credit list modal — the FastCMS-styled port of V1's "Rebill/Credit
 * Info For {pro}" popup. Lists a PRO's rebill/credit records (Rebill Date |
 * Credit Memo # | Rebilled Invoice #); the Rebill Date cell is a link that
 * opens that record for editing. Deleted rows are shown in red (V1 parity).
 *
 * Portals to <body> at z-[130] so it layers above the Pro slide-over
 * (z-[100]); an Escape handler runs in the capture phase and stops propagation
 * so the key closes this modal without also closing the drawer (mirrors
 * ProMarginModal).
 */
interface Props {
  open: boolean;
  pronumber: number;
  onClose: () => void;
  onOpenRecord: (recno: number) => void;
  refreshKey?: number;
}

const TH =
  'text-left text-[0.6875rem] font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400';
const ROW_BORDER = 'border-t border-slate-100 dark:border-gray-700/60';

const RebillCreditListModal: React.FC<Props> = ({
  open,
  pronumber,
  onClose,
  onOpenRecord,
  refreshKey,
}) => {
  const [records, setRecords] = useState<RebillCreditListRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Capture-phase Escape: close THIS modal and stop the event before it reaches
  // the slide-over's own Escape handler (which would close the drawer).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  // Fetch on open (and whenever refreshKey changes while open).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    rebillCreditService
      .list(pronumber)
      .then((res) => {
        if (cancelled) return;
        setRecords(res.records);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Failed to load rebill/credit records.');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, pronumber, refreshKey]);

  // List view: Alt+C closes. No save of its own — editing happens in the edit
  // modal this opens, which claims the layer above us.
  useAltHotkeys({ C: onClose }, undefined, { layer: open });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Rebill/Credit Info For ${pronumber}`}
      onClick={onClose}
      data-testid="rebill-credit-list-modal"
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-slate-200 bg-panel shadow-xl dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader
          title={`Rebill/Credit Info For ${pronumber}`}
          onClose={onClose}
          closeLabel="Close rebill/credit list"
          dragHandleProps={dragHandleProps}
        />

        {/* Body */}
        <div className="overflow-y-auto px-4 py-3 text-[0.78125rem] text-slate-700 dark:text-gray-200">
          {loading && (
            <p className="py-6 text-center text-slate-400 dark:text-gray-500">Loading…</p>
          )}
          {!loading && error && (
            <p className="py-6 text-center text-red-600 dark:text-red-400">{error}</p>
          )}
          {!loading && !error && records.length === 0 && (
            <p className="py-6 text-center text-slate-400 dark:text-gray-500">
              No rebill/credit records
            </p>
          )}
          {!loading && !error && records.length > 0 && (
            <table className="w-full">
              <thead>
                <tr>
                  <th className={`${TH} py-1`}>Rebill Date</th>
                  <th className={`${TH} py-1`}>Credit Memo #</th>
                  <th className={`${TH} py-1`}>Rebilled Invoice #</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => {
                  const isDeleted = r.deleted === 'Y';
                  return (
                    <tr
                      key={r.recno}
                      className={`${ROW_BORDER} ${
                        isDeleted ? 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/20' : ''
                      }`}
                    >
                      <td className="py-1">
                        <button
                          type="button"
                          onClick={() => onOpenRecord(r.recno)}
                          aria-label={`Open rebill/credit record ${r.recno}`}
                          className={`text-left hover:underline ${
                            isDeleted
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-blue-600 dark:text-blue-400'
                          }`}
                        >
                          {r.rebillDate || '—'}
                        </button>
                      </td>
                      <td className="py-1">{r.creditMemoNum || '—'}</td>
                      <td className="py-1">{r.rebilledInvoice || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t border-slate-200 px-4 py-2.5 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-slate-300 px-3 py-1.5 text-[0.75rem] font-semibold text-slate-700 hover:bg-slate-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default RebillCreditListModal;
